import { useEffect, useState } from 'react'
import {Users, Search, ChevronLeft, ChevronRight, MapPin, X, Phone, Mail, FileText, Linkedin, Github, Instagram, Youtube, Twitter, ExternalLink, Award, BookOpen, Briefcase} from 'lucide-react'
import AdminLayout from '@/components/AdminLayout'
import { lumi } from '@/lib/lumi'

const PAGE_SIZE = 15

interface Collaborator {
  _id: string
  userId?: string
  fullName?: string
  displayName?: string
  headline?: string
  city?: string
  state?: string
  country?: string
  email?: string
  phone?: string
  address?: string
  birthDate?: string
  education?: string
  profileScore?: number
  skills?: string[]
  certifications?: string[]
  languages?: string[]
  experience?: string
  cvUrl?: string
  photoUrl?: string
  salaryExpectation?: string
  availability?: string
  socialLinks?: Record<string, string>
  createdAt?: string
  updatedAt?: string
}

const SOCIAL_ICONS: Record<string, React.ElementType> = {
  linkedin: Linkedin,
  github: Github,
  instagram: Instagram,
  twitter: Twitter,
  youtube: Youtube,
  behance: ExternalLink,
  portfolio: ExternalLink,
}

function Field({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null
  return (
    <div className="min-w-0">
      <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">{label}</p>
      <p className="mt-0.5 text-sm text-slate-800 break-words">{value}</p>
    </div>
  )
}

function TagList({ label, items }: { label: string; items?: string[] }) {
  if (!items?.length) return null
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400 mb-1.5">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {items.map((item, i) => (
          <span key={i} className="rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700 border border-amber-100">
            {item}
          </span>
        ))}
      </div>
    </div>
  )
}

function CollaboratorDetailModal({ collaborator: c, onClose }: { collaborator: Collaborator; onClose: () => void }) {
  const name = c.fullName ?? c.displayName ?? c.email ?? 'Colaborador'
  const initial = name.charAt(0).toUpperCase()
  const socialEntries = Object.entries(c.socialLinks ?? {}).filter(([, v]) => v)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-white px-6 py-4">
          <h2 className="text-lg font-bold text-slate-900">Perfil de colaborador</h2>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Identity */}
          <div className="flex items-center gap-4">
            {c.photoUrl ? (
              <img src={c.photoUrl} alt={name} className="h-16 w-16 rounded-full object-cover border border-slate-200" />
            ) : (
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-amber-400 text-2xl font-bold text-slate-900">
                {initial}
              </div>
            )}
            <div>
              <p className="text-xl font-bold text-slate-900">{name}</p>
              {c.headline && <p className="text-sm text-slate-500">{c.headline}</p>}
              {c.profileScore != null && (
                <div className="mt-1.5 flex items-center gap-2">
                  <div className="h-1.5 w-24 rounded-full bg-slate-100">
                    <div className="h-1.5 rounded-full bg-amber-400" style={{ width: `${c.profileScore}%` }} />
                  </div>
                  <span className="text-xs font-semibold text-amber-600">{c.profileScore}% perfil</span>
                </div>
              )}
            </div>
          </div>

          {/* Datos de contacto y ubicación */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Correo electrónico" value={c.email} />
            <Field label="Teléfono" value={c.phone} />
            <Field label="Fecha de nacimiento" value={c.birthDate} />
            <Field label="Disponibilidad" value={c.availability} />
            <Field label="Pretención salarial mensual" value={c.salaryExpectation} />
            <Field label="Escolaridad" value={c.education} />
            <Field label="Dirección" value={c.address} />
            <Field label="Ciudad" value={c.city} />
            <Field label="Estado" value={c.state} />
            <Field label="País" value={c.country} />
            <Field label="Registro" value={c.createdAt?.slice(0, 10)} />
            <Field label="Última actualización" value={c.updatedAt?.slice(0, 10)} />
          </div>

          {/* Experiencia */}
          {c.experience && (
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Experiencia laboral</p>
              <p className="mt-1 text-sm text-slate-700 leading-relaxed whitespace-pre-line">{c.experience}</p>
            </div>
          )}

          {/* Tags */}
          <TagList label="Habilidades" items={c.skills} />
          <TagList label="Certificaciones" items={c.certifications} />
          <TagList label="Idiomas" items={c.languages} />

          {/* CV */}
          {c.cvUrl && (
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-400 mb-1">Currículum vitae</p>
              <a
                href={c.cvUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700 transition"
              >
                <FileText size={15} />
                Descargar / Ver CV
              </a>
            </div>
          )}

          {/* Redes profesionales */}
          {socialEntries.length > 0 && (
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-400 mb-2">Redes profesionales</p>
              <div className="flex flex-wrap gap-2">
                {socialEntries.map(([net, url]) => {
                  const Icon = SOCIAL_ICONS[net] ?? ExternalLink
                  return (
                    <a
                      key={net}
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700 transition capitalize"
                    >
                      <Icon size={13} />
                      {net}
                    </a>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function AdminColaboradores() {
  const [collaborators, setCollaborators] = useState<Collaborator[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [selected, setSelected] = useState<Collaborator | null>(null)

  useEffect(() => {
    const fetchCollaborators = async () => {
      setLoading(true)
      try {
        const filter = search
          ? { $or: [{ fullName: { $regex: search, $options: 'i' } }, { displayName: { $regex: search, $options: 'i' } }, { headline: { $regex: search, $options: 'i' } }, { email: { $regex: search, $options: 'i' } }] }
          : {}
        const res = await lumi.entities.collaborators.list({
          filter,
          sort: { createdAt: -1 },
          limit: PAGE_SIZE,
          skip: page * PAGE_SIZE,
        })
        setCollaborators(res.list as Collaborator[])
        setTotal(res.total ?? res.list.length)
      } catch (e) {
        console.error('[AdminColaboradores] error:', e)
      } finally {
        setLoading(false)
      }
    }
    fetchCollaborators()
  }, [search, page])

  const totalPages = Math.ceil(total / PAGE_SIZE)

  return (
    <AdminLayout title="Colaboradores" subtitle={`Supervisa todos los perfiles de usuarios colaboradores · ${total} total`}>
      {selected && <CollaboratorDetailModal collaborator={selected} onClose={() => setSelected(null)} />}

      <div className="mb-5 relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(0) }}
          placeholder="Buscar por nombre, perfil o correo…"
          className="w-full max-w-md rounded-lg border border-slate-200 bg-white py-2.5 pl-9 pr-4 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
        />
      </div>

      {loading ? (
        <div className="space-y-2">{[...Array(8)].map((_, i) => <div key={i} className="h-14 animate-pulse rounded-xl bg-slate-100" />)}</div>
      ) : collaborators.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 py-16 text-center">
          <Users size={32} className="mb-3 text-slate-300" />
          <p className="font-semibold text-slate-600">Sin resultados</p>
        </div>
      ) : (
        <>
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-100 bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Colaborador</th>
                  <th className="hidden px-4 py-3 text-left text-xs font-semibold text-slate-500 sm:table-cell">Perfil</th>
                  <th className="hidden px-4 py-3 text-left text-xs font-semibold text-slate-500 md:table-cell">Ubicación</th>
                  <th className="hidden px-4 py-3 text-left text-xs font-semibold text-slate-500 lg:table-cell">Score</th>
                  <th className="hidden px-4 py-3 text-left text-xs font-semibold text-slate-500 lg:table-cell">Registro</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {collaborators.map((c) => (
                  <tr key={c._id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        {c.photoUrl ? (
                          <img src={c.photoUrl} alt="" className="h-8 w-8 rounded-full object-cover border border-slate-200" />
                        ) : (
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-400 text-xs font-bold text-slate-900">
                            {(c.fullName ?? c.displayName ?? c.email ?? 'C').charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-slate-900">{c.fullName ?? c.displayName ?? '—'}</p>
                          {c.email ? <p className="text-xs text-slate-400">{c.email}</p> : null}
                        </div>
                      </div>
                    </td>
                    <td className="hidden px-4 py-3 text-slate-500 sm:table-cell">{c.headline ?? '—'}</td>
                    <td className="hidden px-4 py-3 text-slate-500 md:table-cell">
                      {[c.city, c.state].filter(Boolean).length > 0 ? (
                        <span className="flex items-center gap-1"><MapPin size={12} />{[c.city, c.state].filter(Boolean).join(', ')}</span>
                      ) : '—'}
                    </td>
                    <td className="hidden px-4 py-3 lg:table-cell">
                      {c.profileScore != null ? (
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-20 rounded-full bg-slate-100">
                            <div className="h-1.5 rounded-full bg-amber-400" style={{ width: `${c.profileScore}%` }} />
                          </div>
                          <span className="text-xs text-slate-500">{c.profileScore}%</span>
                        </div>
                      ) : '—'}
                    </td>
                    <td className="hidden px-4 py-3 text-xs text-slate-400 lg:table-cell">{c.createdAt?.slice(0, 10) ?? '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setSelected(c)}
                        className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700 transition"
                      >
                        Ver perfil
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 ? (
            <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
              <p>Página {page + 1} de {totalPages}</p>
              <div className="flex gap-2">
                <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium transition hover:bg-slate-50 disabled:opacity-40"><ChevronLeft size={14} /> Anterior</button>
                <button onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium transition hover:bg-slate-50 disabled:opacity-40">Siguiente <ChevronRight size={14} /></button>
              </div>
            </div>
          ) : null}
        </>
      )}
    </AdminLayout>
  )
}

export default AdminColaboradores
